<template>
  <button
    ref="buttonEl"
    class="base-button"
    :class="[{ isExpanded }, kind, type, size, state, tint]"
    :tabindex="tabindex"
    :aria-label="accessibilityLabel"
    @click="onClick"
    @contextmenu.prevent.stop
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @dblclick.stop
  >
    <span class="left" :class="{ trueColorsIcon }">
      <div v-if="$slots.title" class="title">
        <slot name="title" />
      </div>

      <span v-if="$slots.icon" class="icon">
        <slot name="icon" />
      </span>

      <span v-if="text" ref="textEl" class="text" tabindex="-1">
        {{ text }}
      </span>
    </span>
    <span
      v-if="$slots.right"
      class="right trueColorsIcon"
      :class="{ mac: in_mac() }"
    >
      <slot name="right" />
    </span>
  </button>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
  useSlots,
} from "vue";

import { Tint } from "@/components/base/types";
import { TooltipContent, useTooltip } from "@/components/tooltip";
import { isEllipsisActive } from "@/components/utils";
import { in_mac } from "@/utils/in";

export type ButtonKind = "important" | "main" | "auxiliary";

export type ButtonType = "primary" | "secondary" | "tertiary";

type ButtonSize = "full" | "text-only" | "icon-only";

export type ButtonState =
  | "default"
  | "disabled"
  | "loading"
  | "hidden"
  | "active"
  | "selected";

export type TooltipDisplay = "ellipsis" | "always" | "none";

export default defineComponent({
  props: {
    accessibilityLabel: {
      type: String,
      required: false,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    kind: {
      type: String as PropType<ButtonKind>,
      required: true,
    },
    type: {
      type: String as PropType<ButtonType>,
      default: "primary",
    },
    state: {
      type: String as PropType<ButtonState>,
      default: "default",
    },
    text: {
      type: String,
      default: null,
    },
    tooltip: {
      type: [Object, String] as PropType<TooltipContent>,
      required: false,
    },
    tooltipDisplay: {
      type: String as PropType<TooltipDisplay>,
      default: "none",
    },
    trueColorsIcon: { type: Boolean, default: false },
    tint: { type: String as PropType<Tint>, default: "none" },
    tabindex: { type: Number, default: 0 },
  },
  emits: ["click"],
  setup(props, { emit }) {
    const { text, tooltip, tooltipDisplay, state } = toRefs(props);
    const buttonEl = ref<HTMLButtonElement>();
    const textEl = ref<HTMLElement>();
    onBeforeUnmount(() => {
      buttonEl.value = undefined;
      textEl.value = undefined;
    });
    const isTooltipNeeded = computed<boolean>(() => {
      switch (tooltipDisplay.value) {
        case "none":
          return false;
        case "always":
          return true;
        case "ellipsis":
          return isEllipsisActive(textEl.value || null);
        default:
          return false;
      }
    });
    const onClick = (e: MouseEvent) => {
      if (state.value !== "disabled" && state.value !== "loading") {
        emit("click", e);
      }
    };
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(buttonEl, tooltip, "top", isTooltipNeeded);
    onBeforeUnmount(releaseTooltip);
    const slots = useSlots();
    // Beware that the following computed might not be reactive on the slot.
    // For now we do not care as it should be reactive on text.value.
    const size = computed<ButtonSize>(() =>
      slots.title
        ? "full"
        : text.value && text.value !== "" && slots.icon
          ? "full"
          : slots.icon
            ? "icon-only"
            : "text-only"
    );
    return {
      buttonEl,
      hideTooltip,
      in_mac,
      onClick,
      showTooltip,
      size,
      textEl,
    };
  },
});
</script>
<style scoped lang="scss">
button.base-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 7px;
  border-width: 1px;
  border-style: solid;
  @extend %font-body-regular;
  margin-top: auto;
  margin-bottom: auto;
  overflow: hidden;
  width: fit-content;
  justify-content: space-between;
  @include transition(all 100ms ease-out);
  transition-property: color, background-color, border;
  &:focus-visible {
    @include themed() {
      outline: 2px solid t($grey-20);
    }
  }
  .left {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;
  }
  .right {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;
    column-gap: 10px;
    margin-inline-start: 8px;
  }
  &.isExpanded {
    width: 100%;
  }
  &.hidden {
    opacity: 0;
  }
  .text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .icon,
  :deep(svg) {
    display: flex;
    align-items: center;
    height: 14px;
    width: 14px;
  }
  &.important {
    border-radius: 9px;
    .left {
      column-gap: 9px;
    }
    &.full,
    &.text-only {
      padding: 6px 12px;
    }
    &.icon-only {
      flex-shrink: 0;
      padding: 12.5px;
    }
  }
  &.main {
    .left {
      column-gap: 6px;
    }
    &.full,
    &.text-only {
      padding: 3px 10px 4px 10px;
    }
    &.icon-only {
      flex-shrink: 0;
      padding: 10px;
      .icon,
      :deep(svg) {
        height: 8px;
        width: 8px;
      }
    }
  }
  &.auxiliary {
    @extend %font-caption-regular;
    border-color: transparent;
    .left {
      column-gap: 8px;
    }
    .text {
      letter-spacing: 0px;
    }
    &.full,
    &.text-only {
      padding: 4px 8px;
    }
    &.icon-only {
      flex-shrink: 0;
      justify-content: center;
      align-items: center;
      padding: 7.5px;
    }
  }

  &.none {
    &.important,
    &.main {
      // for main CTAs, there should be only one
      &.primary {
        @include themed() {
          background: t($primary);
          border-color: t($primary);
          color: t($bg);
          box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.15);
        }
        :deep(.left:not(.trueColorsIcon)) svg {
          .fill {
            @include themed() {
              fill: t($bg);
            }
          }
          .stroke {
            @include themed() {
              stroke: t($bg);
            }
          }
        }
        &:hover:not(.disabled):not(.loading) {
          @include themed() {
            background: t($primary-hover);
            border-color: t($primary-hover);
          }
        }
        &:active:not(.disabled):not(.loading),
        &.active {
          @include themed() {
            border-color: t($primary-active);
            background: t($primary-active);
          }
        }
        &.disabled,
        &.loading {
          @include themed() {
            background: t($grey-10);
            border-color: t($grey-10);
            color: t($grey-20);
            box-shadow: none;
          }
        }
      }

      // for secondary actions next to CTAs
      &.secondary {
        &.default,
        &.disabled,
        &.loading {
          @include themed() {
            background: t($bg);
            color: t($grey-80);
          }
          :deep(.left:not(.trueColorsIcon)) svg {
            .fill {
              @include themed() {
                fill: t($grey-80);
              }
            }
            .stroke {
              @include themed() {
                stroke: t($grey-80);
              }
            }
          }
        }
        &.default {
          @include themed() {
            border-color: t($grey-30);
          }
        }
        &:hover:not(.disabled):not(.loading) {
          @include themed() {
            color: t($primary);
            border-color: t($grey-50);
          }
        }
        &.disabled,
        &.loading {
          @include themed() {
            border-color: t($grey-15);
            color: t($grey-30);
          }
        }
        &:active:not(.disabled):not(.loading),
        &.active {
          @include themed() {
            color: t($grey-80);
            background: t($grey-05);
          }
        }
      }

      // for less important actions, next to CTAs (cancel buttons, etc.)
      &.tertiary {
        border-color: transparent;
        background: transparent;

        &.default {
          @include themed() {
            color: t($grey-60);
          }
        }
        &:hover:not(.disabled):not(.loading) {
          @include themed() {
            color: t($grey-80);
            background: t($grey-10);
          }
        }
        &:active:not(.disabled):not(.loading),
        &.active {
          @include themed() {
            background: t($grey-10);
          }
        }
      }
    }

    &.auxiliary {
      &.default {
        @include themed() {
          color: t($grey-70);
        }
        :deep(.left:not(.trueColorsIcon)) svg {
          .fill {
            @include themed() {
              fill: t($grey-70);
            }
          }
          .stroke {
            @include themed() {
              stroke: t($grey-70);
            }
          }
        }
      }
      &:hover:not(.disabled):not(.loading),
      &:active:not(.disabled):not(.loading),
      &.active {
        @include themed() {
          color: t($grey-80);
        }
        :deep(.left:not(.trueColorsIcon)) svg {
          .fill {
            @include themed() {
              fill: t($grey-80);
            }
          }
          .stroke {
            @include themed() {
              stroke: t($grey-80);
            }
          }
        }
      }
      &.disabled {
        .text {
          opacity: 1;
        }
        @include themed() {
          color: t($grey-30);
        }
        :deep(.left:not(.trueColorsIcon)) svg {
          .fill {
            @include themed() {
              fill: t($grey-30);
            }
          }
          .stroke {
            @include themed() {
              stroke: t($grey-30);
            }
          }
        }
      }
      &.loading {
        .text {
          opacity: 1;
        }
        @include themed() {
          color: t($grey-50);
        }
        :deep(.left:not(.trueColorsIcon)) svg {
          .fill {
            @include themed() {
              fill: t($grey-50);
            }
          }
          .stroke {
            @include themed() {
              stroke: t($grey-50);
            }
          }
        }
      }
      &.selected {
        @include themed() {
          background: t($grey-05);
        }
      }

      &:hover:not(.disabled):not(.loading):not(.selected),
      &.loading {
        @include themed() {
          background: t($bg-menu-hover);
        }
      }
      &:active:not(.disabled):not(.loading):not(.selected),
      &.active {
        @include themed() {
          background: t($grey-10);
        }
      }
    }
  }

  &.red {
    @include themed() {
      color: t($secondary);
    }

    &.main,
    &.important {
      @include themed() {
        border-color: t($secondary-30);
      }
    }
    :deep(.left:not(.trueColorsIcon)) svg {
      .fill {
        @include themed() {
          fill: t($secondary);
        }
      }
      .stroke {
        @include themed() {
          stroke: t($secondary);
        }
      }
    }
    &:hover:not(.disabled):not(.loading) {
      @include themed() {
        background: t($bg-base-button-hover-red);
      }
    }
    &:active:not(.disabled):not(.loading),
    &.active {
      @include themed() {
        background: t($bg-base-button-active-red);
      }
    }
    // A disabled red action stays red but washed out ($secondary-30, the
    // red counterpart of the $grey-30 disabled text): a full-red but inert
    // button would read as clickable.
    &.disabled {
      @include themed() {
        color: t($secondary-30);
      }
      :deep(.left:not(.trueColorsIcon)) svg {
        .fill {
          @include themed() {
            fill: t($secondary-30);
          }
        }
        .stroke {
          @include themed() {
            stroke: t($secondary-30);
          }
        }
      }
    }
  }

  &.blue {
    @include themed() {
      color: t($tertiary-50);
    }
    &.main,
    &.important {
      @include themed() {
        border-color: t($tertiary-20);
      }
    }
    :deep(.left:not(.trueColorsIcon)) svg {
      .fill {
        @include themed() {
          fill: t($tertiary-50);
        }
      }
      .stroke {
        @include themed() {
          stroke: t($tertiary-50);
        }
      }
    }
    &.default,
    &.disabled,
    &.loading {
      @include themed() {
        background: t($tertiary-10);
      }
    }
    &:hover:not(.disabled):not(.loading) {
      @include themed() {
        background: t($bg-base-button-hover-blue);
      }
    }
    &:active:not(.disabled):not(.loading),
    &.active {
      @include themed() {
        background: t($bg-base-button-active-blue);
      }
    }
  }
}
</style>
